/**
 * ffmpeg/ffprobe utility wrappers
 *
 * Detects system binaries and provides promisified exec helpers.
 * No npm dependency — uses system ffmpeg/ffprobe directly.
 */

import { execFile as execFileCb } from 'node:child_process'
import { promisify } from 'node:util'
import type { Logger } from 'pino'

const execFile = promisify(execFileCb)

export interface FfmpegEncoders {
  libwebp: boolean
  libx264: boolean
  libvpx: boolean
  libopus: boolean
  aac: boolean
}

export interface FfmpegAvailability {
  ffmpeg: boolean
  ffprobe: boolean
  version?: string
  encoders: FfmpegEncoders
}

const NO_ENCODERS: FfmpegEncoders = {
  libwebp: false, libx264: false, libvpx: false, libopus: false, aac: false
}

let availability: FfmpegAvailability | null = null

/**
 * Detects ffmpeg/ffprobe binaries and which encoders are compiled in.
 * Cached after first call.
 */
export async function detectFfmpeg(logger?: Logger): Promise<FfmpegAvailability> {
  if (availability) return availability

  let ffmpegOk = false
  let ffprobeOk = false
  let version: string | undefined
  let encoders: FfmpegEncoders = { ...NO_ENCODERS }

  try {
    const { stdout } = await execFile('ffmpeg', ['-version'])
    ffmpegOk = true
    version = stdout.split('\n')[0]?.match(/ffmpeg version (\S+)/)?.[1]
  } catch { /* not found */ }

  try {
    await execFile('ffprobe', ['-version'])
    ffprobeOk = true
  } catch { /* not found */ }

  if (ffmpegOk) {
    try {
      const { stdout } = await execFile('ffmpeg', ['-hide_banner', '-encoders'], { maxBuffer: 2 * 1024 * 1024 })
      encoders = {
        libwebp: /\blibwebp\b/.test(stdout),
        libx264: /\blibx264\b/.test(stdout),
        libvpx: /\blibvpx(-vp9)?\b/.test(stdout),
        libopus: /\blibopus\b/.test(stdout),
        aac: /^\s*[A-Z.]+\s+aac\s/m.test(stdout)
      }
    } catch { /* leave defaults */ }
  }

  availability = { ffmpeg: ffmpegOk, ffprobe: ffprobeOk, version, encoders }

  if (logger) {
    if (ffmpegOk) {
      const missing = Object.entries(encoders).filter(([, ok]) => !ok).map(([k]) => k)
      logger.info({ version, encoders }, 'ffmpeg detected')
      if (missing.length) {
        logger.warn({ missing }, 'ffmpeg missing encoders — related features will be unavailable')
      }
    } else {
      logger.warn('ffmpeg not found — video processing disabled')
    }
  }

  return availability
}

/** Resets cached detection (for testing) */
export function resetFfmpegDetection(): void {
  availability = null
}

/**
 * Runs ffprobe and returns parsed JSON output.
 */
export async function runFfprobe(inputPath: string): Promise<Record<string, unknown>> {
  const { stdout } = await execFile('ffprobe', [
    '-v', 'quiet',
    '-print_format', 'json',
    '-show_format',
    '-show_streams',
    inputPath
  ], { maxBuffer: 10 * 1024 * 1024 })

  return JSON.parse(stdout) as Record<string, unknown>
}

/**
 * Runs ffmpeg with given args.
 * All output operations write to a file path (NEVER pipe:1).
 * -y flag auto-added to overwrite without prompt.
 */
export async function runFfmpeg(args: string[]): Promise<void> {
  await execFile('ffmpeg', ['-y', ...args], { maxBuffer: 1024 * 1024 })
}

/**
 * Parses a timestamp string like "5s", "1.5", "1m30s" to seconds.
 */
export function parseTimestamp(value: string | undefined): number {
  if (!value) return 0
  const secondsMatch = value.match(/^(\d+(?:\.\d+)?)s?$/)
  if (secondsMatch) return parseFloat(secondsMatch[1]!)
  const minSecMatch = value.match(/^(\d+)m(\d+)s?$/)
  if (minSecMatch) return parseInt(minSecMatch[1]!, 10) * 60 + parseInt(minSecMatch[2]!, 10)
  return 0
}
